import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CallToAction = () => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [emailSubmitted, setEmailSubmitted] = useState(false);

  const contactOptions = [
    {
      id: 'project',
      title: 'Start a Project',
      description: 'Ready to bring your vision to life? Let\'s discuss your project requirements and create something amazing together.',
      icon: 'Rocket',
      color: 'from-blue-500 to-cyan-500',
      action: 'Get Started',
      href: '/contact'
    },
    {
      id: 'collaborate',
      title: 'Collaborate',
      description: 'Looking for a technical partner? I\'m always interested in collaborating on innovative projects and startups.',
      icon: 'Users',
      color: 'from-green-500 to-emerald-500',
      action: 'Let\'s Connect',
      href: '/contact'
    },
    {
      id: 'hire',
      title: 'Hire Me',
      description: 'Seeking a full-stack developer for your team? Let\'s explore how I can contribute to your organization\'s success.',
      icon: 'Briefcase',
      color: 'from-purple-500 to-pink-500',
      action: 'View Resume',
      href: '/about'
    }
  ];

  const socialLinks = [
    { name: 'GitHub', icon: 'Github', url: 'https://github.com/mustan-dev', color: 'hover:text-gray-900' },
    { name: 'LinkedIn', icon: 'Linkedin', url: 'https://www.linkedin.com/in/mustan-aliev-774732382/', color: 'hover:text-blue-600' },
    { name: 'Twitter', icon: 'Twitter', url: 'https://twitter.com/', color: 'hover:text-blue-400' },
    { name: 'Instagram', icon: 'Instagram', url: 'https://www.instagram.com/mustan.dev/', color: 'hover:text-pink-500' },
    { name: 'Email', icon: 'Mail', url: 'mailto:info@mustan.dev', color: 'hover:text-red-500' }
  ];

  const handleEmailSubmit = (e) => {
    e?.preventDefault();
    setEmailSubmitted(true);
    setTimeout(() => setEmailSubmitted(false), 3000);
  };

  return (
    <section className="py-20 bg-gradient-to-br from-primary/5 via-background to-accent/5 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute top-20 left-10 w-32 h-32 border border-primary/10 rounded-full"
          animate={{
            rotate: 360,
            scale: [1, 1.1, 1]
          }}
          transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
        />
        <motion.div
          className="absolute bottom-20 right-10 w-24 h-24 bg-accent/5 rounded-lg"
          animate={{
            rotate: -360,
            y: [0, -20, 0]
          }}
          transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>
      <div className="max-w-7xl mx-auto relative z-10 px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-foreground mb-6">
            Ready to Build
            <span className="block text-gradient-brand">Something Amazing?</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Whether you're a startup with a groundbreaking idea, an enterprise seeking
            digital transformation, or a fellow developer looking to collaborate,
            I'm here to turn your vision into reality.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-3 mb-16">
          {contactOptions?.map((option, index) => (
            <motion.div
              key={option?.id}
              className="group relative"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              viewport={{ once: true }}
              onMouseEnter={() => setHoveredCard(option?.id)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div className="relative h-full rounded-2xl border border-border bg-card p-8 shadow-brand transition-brand hover:shadow-brand-lg">
                <motion.div
                  className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${option?.color} opacity-0 transition-brand group-hover:opacity-5`}
                  animate={{ opacity: hoveredCard === option?.id ? 0.05 : 0 }}
                />

                <motion.div
                  className={`mb-6 flex h-16 w-16 items-center justify-center rounded-xl bg-gradient-to-r ${option?.color} transition-brand group-hover:scale-110`}
                  whileHover={{ rotate: 5 }}
                >
                  <Icon name={option?.icon} size={28} color="white" />
                </motion.div>

                <h3 className="mb-4 text-xl font-bold text-foreground transition-brand group-hover:text-primary">
                  {option?.title}
                </h3>
                <p className="mb-8 leading-relaxed text-muted-foreground">
                  {option?.description}
                </p>

                <Button
                  variant="outline"
                  iconName="ArrowRight"
                  iconPosition="right"
                  className="w-full transition-brand group-hover:border-primary/50 group-hover:text-primary"
                >
                  {option?.action}
                </Button>

                <motion.div
                  className="pointer-events-none absolute inset-0 rounded-2xl"
                  animate={{
                    rotateX: hoveredCard === option?.id ? 2 : 0,
                    rotateY: hoveredCard === option?.id ? 2 : 0,
                    scale: hoveredCard === option?.id ? 1.02 : 1
                  }}
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                />
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="mb-16 rounded-2xl border border-border bg-card p-8 shadow-brand lg:p-12"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="mx-auto max-w-3xl text-center">
            <div className="mb-6 flex items-center justify-center space-x-3">
              <Icon name="Mail" size={24} className="text-primary" />
              <h3 className="text-2xl font-bold text-foreground">Stay Updated</h3>
            </div>
            <p className="mb-8 text-lg leading-relaxed text-muted-foreground">
              Get the latest insights on web development, project updates, and industry trends
              delivered straight to your inbox. No spam, just valuable content.
            </p>
            <form onSubmit={handleEmailSubmit} className="mx-auto flex max-w-md flex-col gap-4 sm:flex-row">
              <input
                type="email"
                required
                placeholder="Enter your email address"
                className="w-full rounded-lg border border-border bg-background px-4 py-3 text-foreground placeholder-muted-foreground focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary transition-brand"
              />
              <Button
                type="submit"
                variant="default"
                iconName={emailSubmitted ? 'Check' : 'Send'}
                iconPosition="right"
                disabled={emailSubmitted}
                className={`px-6 py-3 ${emailSubmitted ? 'bg-green-500 hover:bg-green-600' : 'bg-gradient-brand'}`}
              >
                {emailSubmitted ? 'Subscribed!' : 'Subscribe'}
              </Button>
            </form>
            {emailSubmitted && (
              <motion.p
                className="mt-4 text-sm text-green-500"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                Thank you for subscribing! You'll hear from me soon.
              </motion.p>
            )}
          </div>
        </motion.div>

        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h4 className="mb-6 text-lg font-semibold text-foreground">Or connect with me directly</h4>
          <div className="mb-8 flex items-center justify-center space-x-6">
            {socialLinks?.map((social, index) => (
              <motion.a
                key={social?.name}
                href={social?.url}
                className={`rounded-full bg-muted p-3 text-muted-foreground transition-brand hover:bg-muted/80 ${social?.color}`}
                whileHover={{ scale: 1.1, y: -2 }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Icon name={social?.icon} size={20} />
              </motion.a>
            ))}
          </div>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row sm:gap-6">
            <Button
              variant="default"
              size="lg"
              iconName="Calendar"
              iconPosition="left"
              className="bg-gradient-brand px-8 py-4 hover:shadow-brand"
            >
              Schedule a Call
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="Download"
              iconPosition="left"
              className="px-8 py-4"
            >
              Download Resume
            </Button>
          </div>
          <motion.p
            className="mt-8 text-lg text-muted-foreground"
            animate={{ opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            Let's build the future together, one line of code at a time.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

export default CallToAction;
